import { useCallback, useEffect, useMemo, useState } from 'react';
import { ClipAPI } from '../../api/api';
import type { SceneAlternate } from '../../api/clip';
import { useToast } from '../../hooks/useToast';
import { isWaiting } from '../clips/sections/MediaEditorSection.helpers';
import { EditorMedia } from './editorStore';

/**
 * The takes of one scene block: fetched, filtered, and pickable.
 *
 * Extracted from `SceneAlternatesStrip` so the state can be HOISTED: the
 * storyboard panel now renders the same list twice — as the thumbnail strip and
 * as ‹ › arrows overlaid on the scene's poster — and two components each
 * fetching their own copy would race each other's pending flags and double
 * every request. One hook instance per medium, both surfaces render from it.
 *
 * Two things this hook does that the strip's inline version did not:
 *
 *   - Failures SURFACE. The old code read every fetch error as "no history"
 *     and hid the strip, which made a broken backend indistinguishable from a
 *     clip with none — the takes were there, the UI just silently gave up.
 *     `error` + `retry` let the strip render a one-line retry instead; the
 *     inspector around it still cannot break, because nothing here throws.
 *   - Placeholder takes are filtered. A failed or still-running generation
 *     lists under a `_failed.png`/`_waiting.png` URL; picking one would swap
 *     the clip onto the failure placeholder itself. The backend filters these
 *     too now, but the client must not depend on every deploy having caught
 *     up — the same `isWaiting` the storyboard uses decides here.
 */

export interface SceneAlternatesState {
  /**
   * The block's own medium only, newest first, exactly one marked current
   * (see the implicit-head note below). Empty while loading and after an
   * error — `error` says which.
   */
  takes: SceneAlternate[];
  /** media_id of the take a pick is in flight for; '' when idle. */
  pending: string;
  /** True when the last fetch failed. `retry` refetches. */
  error: boolean;
  retry: () => void;
  /** Binds a take by its origin: select-variant for `take`, swap for `run`. */
  pick: (alternate: SceneAlternate) => Promise<void>;
}

export interface UseSceneAlternatesOptions {
  clipId: string;
  /** The bound block. `null` (scene lacks this medium) leaves the hook inert. */
  slide: EditorMedia | null | undefined;
  /** Position of the slide's item in its clip bucket — the swap's legacy fallback. */
  mediaIndex: number;
  /**
   * Runs after a successful pick, with the media id now bound to the block.
   *
   * For a take that is the block's OWN id — select-variant never changes it —
   * so a caller re-selecting on this value is a no-op rather than a jump.
   */
  onPicked: (boundMediaId: string) => void | Promise<void>;
  /**
   * `false` skips the fetch entirely — for a strip handed a hoisted `state`,
   * whose own mandatory hook call must not fire a duplicate request.
   */
  enabled?: boolean;
}

/** Takes are offered only for the block's own medium. */
const wantedType = (kind: EditorMedia['kind']): string => (kind === 'video' ? 'video' : 'image');

export const useSceneAlternates = ({
  clipId,
  slide,
  mediaIndex,
  onPicked,
  enabled = true,
}: UseSceneAlternatesOptions): SceneAlternatesState => {
  const toast = useToast();
  const [alternates, setAlternates] = useState<SceneAlternate[]>([]);
  const [pending, setPending] = useState('');
  const [error, setError] = useState(false);
  const [fetchNonce, setFetchNonce] = useState(0);

  const slideId = slide?.id ?? '';
  const slideFileUrl = slide?.fileUrl ?? '';
  const type = slide ? wantedType(slide.kind) : 'image';
  const active = enabled && Boolean(slideId);

  useEffect(() => {
    if (!active) return;
    let live = true;
    const load = async () => {
      try {
        const items = (await ClipAPI.listSceneAlternates(clipId, slideId)) || [];
        if (!live) return;
        // Medium filter first, then the placeholder filter: a `_failed.png`
        // row is not a take, it is the tombstone of one.
        setAlternates(items.filter((item) => item.type === type && !isWaiting(item.url)));
        setError(false);
      } catch {
        if (!live) return;
        // Cleared rather than kept: stale takes under a fresh error would
        // offer picks of a list the backend no longer stands behind.
        setAlternates([]);
        setError(true);
      }
    };
    void load();
    return () => {
      live = false;
    };
    // slideFileUrl is what makes this fetch complete the loop it is part of. On
    // the takes path the canonical id never changes, so `slideId` is stable and
    // a keyed remount never happens — a regenerate (or a pick) would leave the
    // list showing the pre-change ledger with the superseded take still marked
    // current. A rebind always changes the URL, and this fetch cannot change it
    // back, so there is no loop.
  }, [active, clipId, slideId, slideFileUrl, type, fetchNonce]);

  const retry = useCallback(() => setFetchNonce((nonce) => nonce + 1), []);

  const pick = useCallback(
    async (alternate: SceneAlternate) => {
      if (!slide || alternate.current || pending) return;
      setPending(alternate.media_id);
      try {
        if (alternate.origin === 'take') {
          // Same id, different bytes. `slideId` stays bound.
          // preserveDuration: a regenerate auto-swaps this item onto its new
          // take without touching the scene's declared length, so picking a
          // neighbouring thumbnail must not change it either. One list, one
          // behaviour.
          await ClipAPI.selectVariant(clipId, slideId, alternate.media_id, {
            preserveDuration: true,
          });
          await onPicked(slideId);
        } else {
          await ClipAPI.swapClipMedia(clipId, {
            kind: slide.kind === 'video' ? 'ai_video' : 'image',
            media_index: Math.max(0, mediaIndex),
            new_media_id: alternate.media_id,
            // The slot named by the id it holds: the index above is computed
            // from a delivered array whose order the store never guaranteed,
            // so on its own it can rebind a DIFFERENT scene's slot.
            old_media_id: slideId,
          });
          await onPicked(alternate.media_id);
        }
      } catch (pickError: any) {
        toast({
          text: `Couldn't switch to that take — ${pickError?.message || pickError}`,
          level: 'error',
        });
      } finally {
        setPending('');
      }
    },
    [clipId, mediaIndex, onPicked, pending, slide, slideId, toast]
  );

  /**
   * The bound take, when the backend could not name it.
   *
   * `POST /clips` mints a FRESH media id for every row and generates new bytes
   * for it, so a just-assembled clip's item is in neither the run's media ids
   * nor its URLs and no row comes back marked current — which would leave a
   * list of takes with none highlighted and the frame the user is actually
   * looking at missing from it. The block itself is the answer: it is the
   * newest take of this scene by construction, so it heads the list. After the
   * first pick the bound id IS an attachment id and the backend's own marker
   * takes over.
   */
  const takes = useMemo<SceneAlternate[]>(() => {
    if (alternates.length === 0 || alternates.some((item) => item.current)) return alternates;
    return [
      { media_id: slideId, url: slideFileUrl, type, current: true, origin: 'take' as const },
      ...alternates.filter((item) => item.media_id !== slideId),
    ];
  }, [alternates, slideFileUrl, slideId, type]);

  return { takes, pending, error, retry, pick };
};

export default useSceneAlternates;
